import { useContext, useMemo, useRef } from "react";
import ScopesList from "../../ScopesList/ScopesList";
import "./ScopeAndGroupsListManagementPopover.scss";
import {
  ScopesContext,
  type ScopeNode,
  type ScopeType,
} from "../../../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import useSelectScope, {
  findScopeSelectionNode,
} from "../../../../../AppView/ScopeManagement/useSelectScope";
import { Link, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../../../../General/AppPreferencesProvider";
import useResourcesCounters from "../../../../../Resources/useResourcesCounters";
import { getIconByType } from "../../../../../Components/Tree/TreeUtils";
import { resourceTypes } from "../../../../../Findings/FindingsUtils";
import {
  Tooltip,
  SkeletonLoader,
  TextButton,
  formatNumber,
  AlertIndicator,
  alertTypes,
} from "@SeemplicityDev/shared";

interface ScopeAndGroupsListManagementPopoverProps {
  editable?: boolean;
  toggleIsOpen: (isOpen?: boolean) => void;
  createNewScope: () => void;
  createNewGroup: () => void;
  selectableNodeType?: string;
  activeListNode?: string | null;
  showHeader?: boolean;
  displayAsInput?: boolean;
}

const ScopeAndGroupsListManagementPopover: React.FC<
  ScopeAndGroupsListManagementPopoverProps
> = ({
  editable,
  toggleIsOpen,
  createNewScope,
  createNewGroup,
  selectableNodeType,
  activeListNode,
  showHeader,
  displayAsInput,
}) => {
  const { isAllowed } = useContext(AppPreferencesContext);
  const {
    scopesState: { scopesList, unscoped },
  } = useContext(ScopesContext);
  const { selectScope } = useSelectScope();

  // At runtime `unscoped` is the flat scope-hierarchy node (a `ScopeNode` with
  // id/name/type/editable), not the nested `SelectedScope` the state type
  // declares. Read it through its true runtime shape.
  const unscopedNode = unscoped as unknown as ScopeNode | null | undefined;

  const scopeManagementMenuRef = useRef<HTMLDivElement>(null);
  let match = useRouteMatch<{ item: string }>("/:item");
  const { t } = useTranslation();
  const noScopes = useMemo(() => {
    return !!scopesList && !scopesList?.[0]?.children?.length;
  }, [scopesList]);

  const {
    resourcesCountData: {
      count: allResourcesCount,
      resourcesCounterLoading: allResourcesCountLoading,
    },
  } = useResourcesCounters({
    selectedScope: null,
    scopeToExecute: null,
    filterToExecute: null,
    shouldSkip: false,
  });

  const {
    resourcesCountData: {
      count: unscopedResourcesCount,
      resourcesCounterLoading: unscopedResourcesCountLoading,
    },
  } = useResourcesCounters({
    selectedScope: { value: { id: unscopedNode?.id } },
    scopeToExecute: null,
    filterToExecute: null,
    shouldSkip: false,
  });

  const coveragePercentage = useMemo(() => {
    if (!!allResourcesCount && !!unscopedResourcesCount) {
      const percent = (unscopedResourcesCount / allResourcesCount) * 100;
      return percent < 10 ? percent.toFixed(2) : Math.round(percent);
    }
  }, [unscopedResourcesCount, allResourcesCount]);

  // Same selection path as picking the unscoped node from the tree.
  const handleShowUnscopedResourcesClick = () => {
    toggleIsOpen(false);

    const node = findScopeSelectionNode(scopesList?.[0], unscopedNode?.id);
    if (!node) {
      return;
    }

    selectScope(node);
  };

  return (
    <div
      ref={scopeManagementMenuRef}
      className={`scope-management-menu-wrapper scope-management-menu-wrapper-class-${match?.params?.item}`}
    >
      {showHeader && (
        <div className={`scope-management-header`}>
          <div className={`scope-management-header-items`}>
            <h3 className={`scopes-tree-header`}>Scopes</h3>
            <div className={`scopes-tree-action-buttons`}>
              {isAllowed(userActionsEnum.CREATE_SCOPE) && (
                <Link to={"/scopes-generator"}>
                  <TextButton
                    label={t("New Scope Hierarchy")}
                    leftIcon={"seem-icon-hierarchy"}
                    data-testid={"go-to-scopes-generator-from-popup"}
                    buttonStyle={"primary"}
                    data-analytics-event="user_click"
                    data-analytics-props='{"name": "new_scope_hierarchy_btn", "container": "scope_management_popover"}'
                  />
                </Link>
              )}
              {isAllowed(userActionsEnum.CREATE_SCOPE) && (
                <TextButton
                  className={"add-group-button"}
                  label={t("New Group")}
                  data-testid={"add-new-group"}
                  leftIcon={getIconByType(resourceTypes.GROUP)}
                  onClick={createNewGroup}
                  data-analytics-event="user_click"
                  data-analytics-props='{"name": "create_group_btn", "container": "scope_management_popover"}'
                />
              )}
              {(isAllowed(userActionsEnum.CREATE_SCOPE) ||
                isAllowed(userActionsEnum.SOFT_EDIT_SCOPE)) && (
                <TextButton
                  onClick={createNewScope}
                  label={t("New Scope")}
                  data-testid={"add-new-scope"}
                  leftIcon={getIconByType(resourceTypes.SCOPE)}
                  data-analytics-event="user_click"
                  data-analytics-props='{"name": "new_scope_btn", "container": "scope_management_popover"}'
                />
              )}
            </div>
          </div>
        </div>
      )}
      {!!unscoped && (
        <div className={"scope-management-unscoped-resources-alert"}>
          <AlertIndicator
            type={alertTypes.GENERAL}
            content={
              <div className={"unscoped-resources-alert-content"}>
                <i className={"seem-icon-unscoped"} />
                {!!allResourcesCount && !!unscopedResourcesCount ? (
                  <div className={"unscoped-resources-alert-title"}>
                    There are{" "}
                    <TextButton
                      buttonStyle={"textLink"}
                      label={`${formatNumber(unscopedResourcesCount)} Unscoped Resources`}
                      onClick={handleShowUnscopedResourcesClick}
                    />{" "}
                    {`(${coveragePercentage}% of Resources)`}
                  </div>
                ) : (
                  (allResourcesCountLoading ||
                    unscopedResourcesCountLoading) && <SkeletonLoader />
                )}
                <Tooltip
                  type="info"
                  content={t(
                    "You have Unscoped Resources. To ensure the best Remediation flow, associate them to Scopes."
                  )}
                >
                  <i className={"seem-icon-help"} />
                </Tooltip>
              </div>
            }
          />
        </div>
      )}
      <div className={`scope-management-content`}>
        <ScopesList
          selectableNodeType={selectableNodeType as ScopeType | undefined}
          selectedNode={activeListNode}
          toggleIsOpen={toggleIsOpen}
          editable={editable}
          noScopes={noScopes}
          scrollToSelected
          displayAsInput={displayAsInput}
        />
      </div>
    </div>
  );
};

export default ScopeAndGroupsListManagementPopover;
